/**
 * Registering this device for push notifications.
 *
 * A subscription belongs to the browser and the device, not to "being signed
 * in" — it is created once, handed to the server against whoever is signed in
 * at that moment (see `POST /push/subscribe`), and then simply sits in the
 * push service until this device unsubscribes or the server stops using it.
 */
import { api } from './api/client'

export type PushState = 'unsupported' | 'off' | 'on'

/**
 * A base64url string, as `VAPID_PUBLIC_KEY` is, into the raw bytes
 * `applicationServerKey` wants.
 *
 * Built with `new Uint8Array(length)` rather than `Uint8Array.from(...)`
 * deliberately: `.from` types its result as backed by `ArrayBufferLike`
 * (which includes `SharedArrayBuffer`), and `applicationServerKey` wants one
 * backed by a plain `ArrayBuffer` specifically.
 */
function urlBase64ToUint8Array(base64: string): Uint8Array<ArrayBuffer> {
  const padding = '='.repeat((4 - (base64.length % 4)) % 4)
  const normalised = (base64 + padding).replace(/-/g, '+').replace(/_/g, '/')
  const raw = atob(normalised)
  const bytes = new Uint8Array(raw.length)
  for (let i = 0; i < raw.length; i++) bytes[i] = raw.charCodeAt(i)
  return bytes
}

function supported(): boolean {
  return 'serviceWorker' in navigator && 'PushManager' in window && 'Notification' in window
}

/**
 * What to show right now, before the person taps anything: `unsupported`
 * covers both "this browser cannot do this" and "the server has no VAPID
 * keypair configured" — either way there is nothing to offer.
 */
export async function pushState(): Promise<PushState> {
  if (!supported()) return 'unsupported'
  if (Notification.permission === 'denied') return 'off'

  const registration = await navigator.serviceWorker.ready
  const existing = await registration.pushManager.getSubscription()
  return existing ? 'on' : 'off'
}

/**
 * Asks for permission, subscribes this device, and tells the server.
 *
 * Returns `'unsupported'` rather than throwing when there is nothing to turn
 * on — no browser support, permission refused, or no keypair configured — so
 * the caller can show one quiet message instead of a caught exception.
 */
export async function enablePush(): Promise<PushState> {
  if (!supported()) return 'unsupported'

  const permission = await Notification.requestPermission()
  if (permission !== 'granted') return 'off'

  const { publicKey } = await api.get<{ publicKey: string | null }>('/push/public-key')
  if (!publicKey) return 'unsupported'

  const registration = await navigator.serviceWorker.ready
  const existing = await registration.pushManager.getSubscription()
  const subscription =
    existing ??
    (await registration.pushManager.subscribe({
      userVisibleOnly: true,
      applicationServerKey: urlBase64ToUint8Array(publicKey),
    }))

  const json = subscription.toJSON()
  await api.post('/push/subscribe', {
    endpoint: json.endpoint,
    keys: { p256dh: json.keys?.p256dh, auth: json.keys?.auth },
  })

  return 'on'
}

export async function disablePush(): Promise<void> {
  if (!('serviceWorker' in navigator)) return
  const registration = await navigator.serviceWorker.ready
  const subscription = await registration.pushManager.getSubscription()
  if (!subscription) return

  // Tell the server first: an unsubscribe that succeeds locally but never
  // reaches the server would leave a dead row for `pushTo` to keep tripping
  // over until the push service itself eventually reports it gone.
  await api.post('/push/unsubscribe', { endpoint: subscription.endpoint })
  await subscription.unsubscribe()
}
